import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { WB_ModelCoefficientRow } from "../PA.Core/PAdbSchemaRows";
import { IwbModelCoefficient } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Model_Coeff_SetR,
    esWB_Model_Coefficient,
    esWB_Model_Coeff_SetCR,
    esWB_Modeling_Session_Trial_PartitionStageSL,
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PAEntity } from "../PA.Core/PAEntity";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { IPolicyCollection } from "../PA.Core/PA.Core.IF.Policy";
import { IHaveSecurity } from "../PA.Core/PA.Core.IF.Aspects";
import { PolicyBOis } from "../PA.Core/Security/PolicyBOis";
import { PolicyHasRole } from "../PA.Core/Security/PolicyHasRole";
import { PolicyCollection } from "../PA.Core/Security/PolicyCollection";
import { ModelCoefficientPolicy } from "./ModelCoefficientPolicy";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";

export class ModelCoefficientBO implements IBusinessObject, IHaveSecurity {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelCoefficient: WB_ModelCoefficientRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Model Coefficient" });

        this.CreateOptions = {};

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
        this.drWB_ModelCoefficient = null;

        this.IHaveSecurity_init();
    }

    get WB_ModelCoefficient(): WB_ModelCoefficientRow {
        return this.drWB_ModelCoefficient;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;

        self.LoadProgressProvider.start("Creating ModelCoefficient");
        let createOpts = _.defaultsDeep({}, self.CreateOptions, options);

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext for new ModelCoefficient.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            self.dbContext
                .createRecordWithKey(esWB_Model_Coefficient)
                .then((modelCoefficientRow: WB_ModelCoefficientRow) => {
                    modelCoefficientRow.setDefaults();
                    self.drWB_ModelCoefficient = modelCoefficientRow;
                    self.dbContext.add(esWB_Model_Coefficient, modelCoefficientRow);
                    self.Identity.setAll(
                        self.drWB_ModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                        self.drWB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                        self.drWB_ModelCoefficient.cif.MODEL_COEFFICIENT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelCoefficient,
                        "WB_MODEL_COEFFICIENT_ID",
                        "MODEL_COEFFICIENT_ID",
                        "MODEL_COEFFICIENT_NAME",
                    );

                    return self.IHaveSecurity_OnLoadComplete();
                })
                .then((ignore) => {
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });
        });

        return self._OnLoadComplete;
    }

    Load(anId: number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let bDone = false;
        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        if (!self.bIsLoaded) self.Identity.setAll(anId, anId, "Loading Model Coefficient Set (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadProgressProvider.start("Loading (" + this.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                let sError = "Model Coefficient already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) {
                self.dbContext = self.LoadOptions.dbContext;
            } else {
                self.dbContext = new PAdbContext();
            }

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Coefficient.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            // Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ModelCoefficientBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = ModelCoefficientBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then((ignore) => {
                    let newModelCoefficientRow: any = recordResolver.get(esWB_Model_Coefficient, anId);

                    if (_.isEmpty(newModelCoefficientRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        bDone = true;
                        return;
                    }

                    self.drWB_ModelCoefficient = newModelCoefficientRow;
                    self.Identity.setAll(
                        self.WB_ModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                        self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                        self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_NAME,
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + this.Identity.name + "(" + this.Identity.id_ui + ")",
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_ModelCoefficient,
                        "WB_MODEL_COEFFICIENT_ID",
                        "MODEL_COEFFICIENT_ID",
                        "MODEL_COEFFICIENT_NAME",
                    );

                    return self.IHaveSecurity_OnLoadComplete();
                })
                .then((ignore) => {
                    if (bDone) return;

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((error: any) => {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(error);
                    reject(error);
                });
        });

        return self._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start(
            "Validating " +
                self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_NAME +
                "(" +
                self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID +
                ")",
        );

        this.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID = _.trim(this.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID);

        let modelCoefficientId = this.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_ID;

        if (_.isEmpty(modelCoefficientId)) {
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                code: "Empty_Id",
                title: "Empty Coefficient Id",
                description: "Model Coefficient Id must not be empty: " + modelCoefficientId,
            });
        }

        let modelIdPattern = /[A-Z]+[A-Z_0-9]*/g;
        let matches = modelCoefficientId.match(modelIdPattern);
        let firstMatch = matches == null ? undefined : matches[0];

        let bValidColumnNameId = matches?.length == 1 && modelCoefficientId == firstMatch;

        if (!bValidColumnNameId) {
            self.ValidateMessageProvider.addMessage({
                type: MessageType.Error,
                code: "Invalid_Id",
                title: "Invalid Coefficient Id",
                description:
                    "Model Coefficient Id must contain uppercase/ underscores letters only, got: " + modelCoefficientId,
            });
        }

        self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) == 0;

        self.ValidateProgressProvider.finish();

        return self;
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            let wbModelCoefficient = self.WB_ModelCoefficient;

            self.SaveProgressProvider.start(
                "Saving " +
                    wbModelCoefficient.cif.MODEL_COEFFICIENT_NAME +
                    "(" +
                    wbModelCoefficient.cif.MODEL_COEFFICIENT_ID +
                    ")",
            );
            self._bIsSaved = false;

            if (_.isEmpty(this.dbContext)) {
                let sError = "Unable to determine dbContext to Save Model Coefficient.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any,
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = this.dbContext.SaveBatch.bind(this.dbContext);
            } else {
                saveFunction = this.dbContext.Save.bind(this.dbContext);
            }

            await self.Validate({});
            if (!self.bIsValidated) {
                self._bIsSaved = false;

                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_Model_Coefficient, this.drWB_ModelCoefficient, {});

            // Create Column using Entity Service
            let aPAEntity = new PAEntity();
            // Create Coefficient Entity based on the Coefficient Type
            switch (self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_TYPE) {
                case "Core":
                    await aPAEntity.createOrUpdateCustomColumn(
                        esWB_Model_Coefficient,
                        wbModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                        "/AZR/WB_MODEL_COEFF_SETR",
                        wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                        {
                            APP_ID: wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                            MAPPER_ID: esWB_Model_Coeff_SetR,
                            SERVER_MODEL_ID: esWB_Model_Coeff_SetR,
                            COLUMN_DATATYPE: "DECIMAL",
                            COLUMN_LENGTH: 15,
                            COLUMN_PRECISION: 6,
                            NOTES: wbModelCoefficient.cif.COMMENTS || "",
                        },
                    );
                    break;

                case "Cannibalization":
                    await aPAEntity.createOrUpdateCustomColumn(
                        esWB_Model_Coefficient,
                        wbModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                        "/AZR/WB_MODEL_COEFF_SETCR",
                        wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                        {
                            APP_ID: wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                            MAPPER_ID: esWB_Model_Coeff_SetCR,
                            SERVER_MODEL_ID: esWB_Model_Coeff_SetCR,
                            COLUMN_DATATYPE: "DECIMAL",
                            COLUMN_LENGTH: 15,
                            COLUMN_PRECISION: 6,
                            NOTES: wbModelCoefficient.cif.COMMENTS || "",
                        },
                    );
                    break;
            }

            await aPAEntity.createOrUpdateCustomColumn(
                esWB_Model_Coefficient,
                wbModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                "/AZR/WB_MODELING_SESSION_TRIALPSSL",
                wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                {
                    APP_ID: wbModelCoefficient.cif.MODEL_COEFFICIENT_ID,
                    MAPPER_ID: esWB_Modeling_Session_Trial_PartitionStageSL,
                    SERVER_MODEL_ID: esWB_Modeling_Session_Trial_PartitionStageSL,
                    COLUMN_DATATYPE: "DECIMAL",
                    COLUMN_LENGTH: 15,
                    COLUMN_PRECISION: 6,
                    NOTES: wbModelCoefficient.cif.COMMENTS || "",
                },
            );

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                    self.IHaveSecurity_AfterSaveComplete();
                    LeaseRegistry.generateLeaseInvalidationByClassList([
                        LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)),
                        LeaseRegistry.getLeaseClass(
                            LeaseRegistry.getLeaseClass(
                                LeaseRegistry.Lease_Subject_Model_Coefficient,
                                this.drWB_ModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                            ),
                        ),
                    ]);
                });
            } else {
                self._bIsSaved = true;
                await self.IHaveSecurity_AfterSaveComplete();
                LeaseRegistry.generateLeaseInvalidationByClassList([
                    LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)),
                    LeaseRegistry.getLeaseClass(
                        LeaseRegistry.getLeaseClass(
                            LeaseRegistry.Lease_Subject_Model_Coefficient,
                            this.drWB_ModelCoefficient.cif.WB_MODEL_COEFFICIENT_ID,
                        ),
                    ),
                ]);
            }

            self.SaveProgressProvider.finish();
        } catch (error) {
            this.SaveProgressProvider.exception(error);
        }

        return self;
    }

    getTableName(): string {
        const self = this;

        switch (self.WB_ModelCoefficient.cif.MODEL_COEFFICIENT_TYPE) {
            case "Core":
                return "/AZR/WB_MODEL_COEFF_SETR";

            case "Cannibalization":
                return "/AZR/WB_MODEL_COEFF_SETCR";
        }
        return "";
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let wbModelCoefficient = <IwbModelCoefficient>this.drWB_ModelCoefficient;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " +
                wbModelCoefficient.MODEL_COEFFICIENT_NAME +
                "(" +
                wbModelCoefficient.MODEL_COEFFICIENT_ID +
                ")",
        );
        return new Promise<IBusinessObject>((resolve, reject) => {
            // 1. Validate if we can delete the Meta

            // 2. If Valid, mark all the records for deletion.
            this.drWB_ModelCoefficient.markDeleted();
            if (bSaveNow) {
                this.Save()
                    .then((result) => {
                        this._bIsDeleted = true;
                        this.DeleteProgressProvider.finish();
                        resolve(result);
                    })
                    .catch((error) => {
                        this.DeleteProgressProvider.exception(error);
                        reject(error);
                    });
            } else {
                this._bIsDeleted = true;
                this.DeleteProgressProvider.finish();
                resolve(this);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc="IHaveSecurity operations">

    private _PolicyCollection: IPolicyCollection;
    private _bIHaveSecurityLoaded: boolean;
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>;

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection();
        this._bIHaveSecurityLoaded = false;
        this._OnIHaveSecurityLoaded = undefined;
    }

    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded.then((ignore) => {
                resolve();
            }).catch((err) => {
                error(err);
            });
        });
    }

    async IHaveSecurity_AfterSaveComplete(): Promise<void> {
        const self = this;

        let policy: ModelCoefficientPolicy = self._PolicyCollection["ModelCoefficient"];

        if (policy) await policy.refreshPolicy(true);
    }

    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this;

        if (self.bIHaveSecurityLoaded) {
            return ASync.resolve(self);
        }

        if (this._OnIHaveSecurityLoaded) {
            return self._OnIHaveSecurityLoaded;
        }

        let aBOis = new PolicyBOis(["Super_User", "Model_Coefficient_Editor"], ["Model_Coefficient_Viewer"]);
        self.PolicyCollection.addPolicy("BOis", aBOis);

        let aHasRolePolicy: PolicyHasRole;
        let aModelCoefficientPolicy: ModelCoefficientPolicy;

        self._OnIHaveSecurityLoaded = new Promise<IHaveSecurity>((resolve, error) => {
            aHasRolePolicy = new PolicyHasRole();
            aHasRolePolicy.OnLoadComplete.then((ignore) => {
                self.PolicyCollection.addPolicy("HasRole", aHasRolePolicy);
                aModelCoefficientPolicy = new ModelCoefficientPolicy(self);
                return aModelCoefficientPolicy.OnLoadComplete;
            })
                .then((ignore) => {
                    self.PolicyCollection.addPolicy("ModelCoefficient", aModelCoefficientPolicy);
                    self._bIHaveSecurityLoaded = true;
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });

        return self._OnIHaveSecurityLoaded;
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection;
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded;
    }

    //</editor-fold>

    //<editor-fold desc="Record Resolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const modelCoefficient_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, modelCoefficient_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Model_Coefficient, ids)
                .then((result) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass(ModelCoefficientBO, "ModelCoefficientBO", "Model Coefficient");
